import React, { useState, useEffect, useCallback } from 'react';
import { X, Plus, Trash2 } from 'lucide-react';

// Admin manager for SystemLookups — the dropdown lists used across the app
// (Religion, MaritalStatus, EmploymentStatus, JobSector, ...). GlobalAdmin
// only; the parent gates the button that opens this.
// onChanged fires after any mutation so the parent can refresh live dropdowns.
const RegistrationLookupsModal = ({ apiCall, showSuccess, showError, onClose, onChanged }) => {
  const [rows, setRows] = useState([]);
  const [cat, setCat] = useState('');
  const [newLookup, setNewLookup] = useState({ category: '', value: '' });

  const loadRows = useCallback(async () => {
    const data = await apiCall('/registration/lookups/manage');
    setRows(data);
    return data;
  }, [apiCall]);

  useEffect(() => {
    loadRows()
      .then(data => setCat(prev => prev || data[0]?.Category || ''))
      .catch(err => showError('Failed to load lists: ' + err.message));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setRowField = (lookupId, field, value) =>
    setRows(rs => rs.map(r => r.LookupID === lookupId ? { ...r, [field]: value } : r));

  const handleAdd = async () => {
    const category = (newLookup.category || cat).trim();
    if (!category || !newLookup.value.trim()) {
      showError('Value is required (and a list name for a new list)');
      return;
    }
    try {
      await apiCall('/registration/lookups', {
        method: 'POST',
        body: JSON.stringify({ category, value: newLookup.value.trim() })
      });
      showSuccess(`Added to ${category}`);
      setNewLookup({ category: '', value: '' });
      setCat(category);
      await loadRows();
      onChanged?.();
    } catch (err) {
      showError(err.message);
    }
  };

  const handleSave = async (row) => {
    try {
      await apiCall(`/registration/lookups/${row.LookupID}`, {
        method: 'PUT',
        body: JSON.stringify({
          value: row.Value,
          sortOrder: parseInt(row.SortOrder) || 50,
          isActive: !!row.IsActive
        })
      });
      showSuccess('Value updated');
      await loadRows();
      onChanged?.();
    } catch (err) {
      showError(err.message);
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Remove "${row.Value}" from ${row.Category}? Families already using it keep their saved text.`)) return;
    try {
      await apiCall(`/registration/lookups/${row.LookupID}`, { method: 'DELETE' });
      showSuccess('Value removed');
      await loadRows();
      onChanged?.();
    } catch (err) {
      showError(err.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-2xl max-h-[85vh] overflow-auto">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold">Dropdown Lists</h3>
          <button onClick={onClose}><X size={20} /></button>
        </div>

        {/* Category tabs */}
        <div className="flex flex-wrap gap-2 mb-4">
          {[...new Set(rows.map(r => r.Category))].map(c => (
            <button
              key={c}
              onClick={() => setCat(c)}
              className={`px-3 py-1 rounded-full text-sm ${
                cat === c ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        {/* Add value / new list */}
        <div className="flex flex-wrap items-end gap-2 mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex-1 min-w-[140px]">
            <label className="block text-xs text-gray-500 mb-1">New value</label>
            <input
              type="text"
              value={newLookup.value}
              onChange={e => setNewLookup(f => ({ ...f, value: e.target.value }))}
              className="w-full border rounded-lg px-2 py-1.5 text-sm"
              placeholder={`Add to ${cat || '...'}`}
            />
          </div>
          <div className="min-w-[140px]">
            <label className="block text-xs text-gray-500 mb-1">New list name <span className="text-gray-400">(optional)</span></label>
            <input
              type="text"
              value={newLookup.category}
              onChange={e => setNewLookup(f => ({ ...f, category: e.target.value }))}
              className="w-full border rounded-lg px-2 py-1.5 text-sm"
              placeholder={cat || 'e.g. Nationality'}
            />
          </div>
          <button
            onClick={handleAdd}
            className="px-4 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 flex items-center gap-1"
          >
            <Plus size={14} /> Add
          </button>
        </div>

        {/* Values of the selected list */}
        <div className="space-y-1.5">
          {rows.filter(r => r.Category === cat).map(row => (
            <div key={row.LookupID} className={`flex items-center gap-2 px-2 py-1.5 rounded-lg ${row.IsActive ? 'bg-gray-50' : 'bg-gray-100 opacity-60'}`}>
              <input
                type="text"
                value={row.Value}
                onChange={e => setRowField(row.LookupID, 'Value', e.target.value)}
                className="flex-1 border rounded px-2 py-1 text-sm"
              />
              <input
                type="number"
                value={row.SortOrder}
                onChange={e => setRowField(row.LookupID, 'SortOrder', e.target.value)}
                className="w-16 border rounded px-2 py-1 text-sm text-right"
                title="Sort order"
              />
              <label className="flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={!!row.IsActive}
                  onChange={e => setRowField(row.LookupID, 'IsActive', e.target.checked)}
                />
                active
              </label>
              <button onClick={() => handleSave(row)} className="text-blue-600 text-xs hover:underline">Save</button>
              <button onClick={() => handleDelete(row)} className="text-red-600 hover:text-red-800" title="Remove">
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          {rows.filter(r => r.Category === cat).length === 0 && (
            <p className="text-sm text-gray-500 italic text-center py-3">No values in this list yet.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default RegistrationLookupsModal;
